import { z } from 'zod';
import { isEmpty, isNil, isNumber } from 'lodash';
import type { Translator } from 'types/injects/translator.type';
import { OcrFormStep, OcrFormType } from 'composables/ocr/types';

const catalogueLinkSchema = z.object({
    id: z.number().optional(),
    value: z.number().optional(),
    label: z.string().optional(),
    itemType: z.union([z.literal('item'), z.literal('service')]).optional(),
});

const productRelatedSchema = z
    .object({
        serialized_stock: z.string().optional(),
    })
    .passthrough();

const formTagSchema = z.object({
    id: z.union([z.number(), z.string()]).nullable(),
    value: z.string(),
});

const paymentSchema = z.object({
    date: z.string(),
    reference: z.string(),
    amount: z.union([z.number(), z.string()]).nullable(),
    payment_mean: z.union([z.number(), z.string()]),
});

const productSchema = z.object({
    description: z.string(),
    quantity: z.union([z.number(), z.string()]).nullable(),
    priceWithoutTax: z.union([z.number(), z.string()]).nullable(),
    unit: z.union([z.string(), z.number()]).nullable(),
    catalogueLink: catalogueLinkSchema.nullable(),
    analyticCode: z.string().nullable(),
    stockOutDate: z.string().nullable(),
    warehouseId: z.union([z.string(), z.number()]).nullable(),
    serialNumbers: z.string().nullable(),
    taxAmountOverride: z.union([z.number(), z.string()]).nullable(),
    amountIncludingTaxOverride: z.union([z.number(), z.string()]).nullable(),
    accountingCode: z.number().nullable(),
    taxRateId: z.number().nullable(),
    related: productRelatedSchema.nullable().optional(),
});

const ocrFormBaseSchema = z.object({
    type: z.nativeEnum(OcrFormType),
    step: z.nativeEnum(OcrFormStep),
    supplier: z.number().nullable(),
    documentDate: z.string(),
    dueDate: z.string(),
    externalDocumentName: z.string(),
    analyticCode: z.string(),
    currency: z.string().nullable(),
    note: z.string(),
    products: z.array(productSchema),
    payments: z.array(paymentSchema),
    tags: z.array(formTagSchema),
    totalWithoutTax: z.number().nullable(),
    totalWithTax: z.number().nullable(),
});

export type OcrFormValues = z.infer<typeof ocrFormBaseSchema>;
export type OcrProduct = z.infer<typeof productSchema>;
export type OcrPayment = z.infer<typeof paymentSchema>;

export const isPaymentPartiallyFilled = (payment: OcrPayment): boolean => {
    const hasDate = !isEmpty(payment.date);
    const hasReference = !isEmpty(payment.reference);
    const hasAmount = !isNil(payment.amount) && payment.amount !== '';
    const hasPaymentMean = isNumber(payment.payment_mean);
    return hasDate || hasReference || hasAmount || hasPaymentMean;
};

const PRICE_PATTERN = /^-?\d*[.,]?\d*$/;

export const createOcrFormSchema = ($t: Translator): z.ZodType<OcrFormValues> => {
    const requiredMessage = $t.__('common.labels.this_field_is_required');
    const documentNumberMaxLengthMessage = $t.__(
        'ocr.OcrVerificationForm.validation.document_number_max_length',
    );

    const addRequired = (ctx: z.RefinementCtx, path: (string | number)[]): void => {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path, message: requiredMessage });
    };

    return ocrFormBaseSchema.superRefine((data, ctx) => {
        if (data.supplier === null) {
            addRequired(ctx, ['supplier']);
        }
        if (data.documentDate === '') {
            addRequired(ctx, ['documentDate']);
        }
        if (data.currency === null || data.currency === '') {
            addRequired(ctx, ['currency']);
        }
        if (data.externalDocumentName.length > 100) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['externalDocumentName'],
                message: documentNumberMaxLengthMessage,
            });
        }

        if (data.type !== OcrFormType.CREDIT_NOTE && !data.dueDate) {
            addRequired(ctx, ['dueDate']);
        }

        for (const [index, product] of data.products.entries()) {
            if (product.description === '') {
                addRequired(ctx, ['products', index, 'description']);
            }
            if (product.accountingCode === null) {
                addRequired(ctx, ['products', index, 'accountingCode']);
            }
            if (product.taxRateId === null) {
                addRequired(ctx, ['products', index, 'taxRateId']);
            }
            if (
                product.priceWithoutTax === null ||
                product.priceWithoutTax === '' ||
                !PRICE_PATTERN.test(String(product.priceWithoutTax))
            ) {
                addRequired(ctx, ['products', index, 'priceWithoutTax']);
            }
        }

        for (const [index, payment] of data.payments.entries()) {
            if (!isPaymentPartiallyFilled(payment)) {
                continue;
            }
            if (isEmpty(payment.date)) {
                addRequired(ctx, ['payments', index, 'date']);
            }
            if (isNil(payment.amount) || payment.amount === '') {
                addRequired(ctx, ['payments', index, 'amount']);
            }
            if (!isNumber(payment.payment_mean)) {
                addRequired(ctx, ['payments', index, 'payment_mean']);
            }
        }
    });
};
